import React, { useState, useEffect, useContext, useCallback } from "react";
import { Modal, Button, Alert, Spinner } from "react-bootstrap";
import { IconUser, IconCreditCard, IconCheck, IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import UserContext from "../../../contexts/UserContext";
import CustomerProfileServices from "../../../services/CustomerProfileServices";
import CustomerBillingProfileServices from "../../../services/CustomerBillingProfileServices";
import CustomerProfileStep from "./steps/CustomerProfileStep";
import BillingProfileStep from "./steps/BillingProfileStep";

const CustomerProfileModal = ({ show, onHide, profileId, mode = "create", onSuccess }) => {
  const { user, isOrganization } = useContext(UserContext);

  // Estados principais
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [billingProfile, setBillingProfile] = useState(null);
  // Estados temporários para armazenar dados antes da criação final
  const [tempCustomerData, setTempCustomerData] = useState(null);

  // Formulários separados para cada step
  const customerForm = useForm({
    defaultValues: {
      name: "",
      email: "",
      cpf_cnpj: "",
      phone: "",
    },
  });

  const billingForm = useForm({
    defaultValues: {
      profile_name: "",
      billing_type: "CREDIT_CARD",
      card_holder_name: "",
      card_number: "",
      card_expiry_month: "",
      card_expiry_year: "",
      card_ccv: "",
      address: "",
      address_number: "",
      province: "",
      postal_code: "",
      city: "",
      state: "",
    },
  });

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";

  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Se estamos editando, carregar dados do perfil de faturamento
      const billingResponse = await CustomerBillingProfileServices.getCustomerBillingProfile(profileId);
      const billingData = billingResponse.data;
      setBillingProfile(billingData);

      // Carregar dados do perfil do cliente
      const customerResponse = await CustomerProfileServices.getCustomerProfile(billingData.customer_profile);
      const customerData = customerResponse.data;
      setCustomerProfile(customerData);

      // Preencher formulários
      customerForm.reset({
        name: customerData.name || "",
        email: customerData.email || "",
        cpf_cnpj: customerData.cpf_cnpj || "",
        phone: customerData.phone || "",
      });

      billingForm.reset({
        profile_name: billingData.profile_name || "",
        billing_type: billingData.billing_type || "CREDIT_CARD",
        card_holder_name: billingData.card_holder_name || "",
        card_number: "", // Por segurança, não preenchemos
        card_expiry_month: billingData.card_expiry_month || "",
        card_expiry_year: billingData.card_expiry_year || "",
        card_ccv: "", // Por segurança, não preenchemos
        address: billingData.address || "",
        address_number: billingData.address_number || "",
        province: billingData.province || "",
        postal_code: billingData.postal_code || "",
        city: billingData.city || "",
        state: billingData.state || "",
      });

      // Para edição, começar no step 2 (billing)
      setCurrentStep(2);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os dados do perfil.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  }, [profileId, customerForm, billingForm]);

  const resetForms = useCallback(() => {
    customerForm.reset();
    billingForm.reset();
    setCustomerProfile(null);
    setBillingProfile(null);
    setTempCustomerData(null);
  }, [customerForm, billingForm]);

  useEffect(() => {
    if (show) {
      if ((isEditMode || isViewMode) && profileId) {
        loadProfileData();
      } else {
        resetForms();
        setCurrentStep(1);
      }
    }
  }, [show, profileId, mode, isEditMode, isViewMode, loadProfileData, resetForms]);

  const handleCustomerProfileSubmit = async (data) => {
    try {
      setLoading(true);

      if (isEditMode && customerProfile) {
        // No modo edição, atualizar diretamente
        const response = await CustomerProfileServices.updateCustomerProfile(customerProfile.id, data);
        const customerData = response.data;
        setCustomerProfile(customerData);
      } else {
        // No modo criação, apenas salvar temporariamente
        setTempCustomerData(data);
      }
      
      // Avançar para o próximo step
      setCurrentStep(2);
      
    } catch (error) {
      console.error("Erro ao salvar perfil do cliente:", error);
      
      let errorMessage = "Não foi possível salvar o perfil do cliente.";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.email) {
          errorMessage = `Email: ${Array.isArray(error.response.data.email) ? error.response.data.email[0] : error.response.data.email}`;
        } else if (error.response.data.cpf_cnpj) {
          errorMessage = `CPF/CNPJ: ${Array.isArray(error.response.data.cpf_cnpj) ? error.response.data.cpf_cnpj[0] : error.response.data.cpf_cnpj}`;
        } else if (error.response.data.name) {
          errorMessage = `Nome: ${Array.isArray(error.response.data.name) ? error.response.data.name[0] : error.response.data.name}`;
        } else if (error.response.data.phone) {
          errorMessage = `Telefone: ${Array.isArray(error.response.data.phone) ? error.response.data.phone[0] : error.response.data.phone}`;
        }
      }
      
      Swal.fire({
        icon: "error",
        title: "Erro ao Salvar",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBillingProfileSubmit = async (data) => {
    try {
      setLoading(true);

      let customerData = customerProfile;
      
      // Se estamos no modo criação e não temos um perfil de cliente criado
      if (isCreateMode && !customerProfile && tempCustomerData) {
        // Primeiro, criar o perfil do cliente
        const customerResponse = await CustomerProfileServices.createCustomerProfile(tempCustomerData);
        customerData = customerResponse.data;
        setCustomerProfile(customerData);
      }

      // Adicionar customer_profile ao payload
      const payload = {
        ...data,
        customer_profile: customerData.id,
        internal: 1,
      };

      let billingData;
      if (isEditMode && billingProfile) {
        // Atualizar perfil existente
        const response = await CustomerBillingProfileServices.updateCustomerBillingProfile(billingProfile.id, payload);
        billingData = response.data;
      } else {
        // Criar novo perfil com tokenização
        const response = await CustomerBillingProfileServices.createBillingProfileWithTokenization(payload);
        billingData = response.data;
      }

      setBillingProfile(billingData);
      
      Swal.fire({
        icon: "success",
        title: isEditMode ? "Perfil Atualizado!" : "Perfil Criado!",
        text: isEditMode ? "Perfil de faturamento atualizado com sucesso." : "Perfil de faturamento criado com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      if (onSuccess) onSuccess();
      onHide();
      
    } catch (error) {
      console.error("Erro ao salvar perfil de faturamento:", error);
      
      let errorMessage = "Não foi possível salvar o perfil de faturamento.";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.card_number) {
          errorMessage = `Cartão: ${Array.isArray(error.response.data.card_number) ? error.response.data.card_number[0] : error.response.data.card_number}`;
        } else if (error.response.data.profile_name) {
          errorMessage = `Nome do perfil: ${Array.isArray(error.response.data.profile_name) ? error.response.data.profile_name[0] : error.response.data.profile_name}`;
        } else if (error.response.data.address) {
          errorMessage = `Endereço: ${Array.isArray(error.response.data.address) ? error.response.data.address[0] : error.response.data.address}`;
        } else if (error.response.data.postal_code) {
          errorMessage = `CEP: ${Array.isArray(error.response.data.postal_code) ? error.response.data.postal_code[0] : error.response.data.postal_code}`;
        } else if (error.response.data.email) {
          errorMessage = `Email: ${Array.isArray(error.response.data.email) ? error.response.data.email[0] : error.response.data.email}`;
        }
      }
      
      Swal.fire({
        icon: "error",
        title: "Erro ao Salvar",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      // Validar e submeter customer form
      customerForm.handleSubmit(handleCustomerProfileSubmit)();
    }
  };

  const getModalTitle = () => {
    if (isViewMode) return "Visualizar Perfil";
    if (isEditMode) return "Editar Perfil de Faturamento";
    return "Novo Perfil de Faturamento";
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return isEditMode ? "Dados do Cliente" : "1. Dados do Cliente";
      case 2:
        return isEditMode ? "Perfil de Faturamento" : "2. Perfil de Faturamento";
      default:
        return "";
    }
  };

  const renderStepIndicator = () => {
    if (isEditMode) return null; // Não mostrar indicador no modo edição

    return (
      <div className="d-flex justify-content-center mb-4">
        <div className="d-flex align-items-center">
          <div className={`step-indicator ${currentStep >= 1 ? "active" : ""}`}>
            <IconUser size={20} />
            <span className="ms-2">Cliente</span>
          </div>
          <div className="step-line"></div>
          <div className={`step-indicator ${currentStep >= 2 ? "active" : ""}`}>
            <IconCreditCard size={20} />
            <span className="ms-2">Faturamento</span>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CustomerProfileStep
            form={customerForm}
            isViewMode={isViewMode}
            isEditMode={isEditMode}
            loading={loading}
          />
        );
      case 2:
        return (
          <BillingProfileStep
            form={billingForm}
            isViewMode={isViewMode}
            isEditMode={isEditMode}
            loading={loading}
            customerProfile={customerProfile || (tempCustomerData ? { name: tempCustomerData.name, email: tempCustomerData.email } : null)}
          />
        );
      default:
        return null;
    }
  };

  const renderFooterButtons = () => {
    if (isViewMode) {
      return (
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      );
    }

    return (
      <>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancelar
        </Button>
        
        {currentStep > 1 && !isEditMode && (
          <Button variant="outline-primary" onClick={handlePreviousStep} disabled={loading}>
            <IconArrowLeft size={16} className="me-1" />
            Anterior
          </Button>
        )}
        
        {currentStep === 1 && !isEditMode ? (
          <Button variant="primary" onClick={handleNextStep} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Salvando...
              </>
            ) : (
              <>
                Próximo
                <IconArrowRight size={16} className="ms-1" />
              </>
            )}
          </Button>
        ) : (
          <Button
            variant="success"
            onClick={billingForm.handleSubmit(handleBillingProfileSubmit)}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {isEditMode ? "Atualizando..." : "Criando..."}
              </>
            ) : (
              <>
                <IconCheck size={16} className="me-1" />
                {isEditMode ? "Atualizar Perfil" : "Criar Perfil"}
              </>
            )}
          </Button>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconCreditCard size={24} className="me-2" />
          {getModalTitle()}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && currentStep === 1 ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando dados...</p>
          </div>
        ) : (
          <>
            {renderStepIndicator()}
            
            <div className="mb-3">
              <h5 className="text-primary">{getStepTitle()}</h5>
            </div>
            
            {!isOrganization() ? (
              <Alert variant="warning">
                Esta funcionalidade está disponível apenas para organizações.
              </Alert>
            ) : user?.superuser_level > 2 ? (
              <Alert variant="warning">
                Você não tem permissão para {isEditMode ? "editar" : "criar"} perfis de faturamento.
              </Alert>
            ) : (
              renderCurrentStep()
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        {renderFooterButtons()}
      </Modal.Footer>

      <style jsx>{`
        .step-indicator {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          border: 2px solid #dee2e6;
          border-radius: 25px;
          color: #6c757d;
          background: white;
          transition: all 0.3s ease;
        }
        
        .step-indicator.active {
          border-color: #007bff;
          color: #007bff;
          background: #f8f9fa;
        }
        
        .step-line {
          width: 40px;
          height: 2px;
          background: #dee2e6;
          margin: 0 10px;
        }
      `}</style>
    </Modal>
  );
};

export default CustomerProfileModal;